import React from 'react';
import { Trophy, Medal, Award } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Loader } from '../../components/ui/Loader';
import { useAuth } from '../../contexts/AuthContext';
import { useLeaderboard, useResultsBySchool } from '../../hooks/useConvex';
import { Id } from '../../../convex/_generated/dataModel';

export const ResultsPage: React.FC = () => {
    const { school } = useAuth();
    const allResults = useResultsBySchool(school?._id as Id<'schools'> | undefined);
    const leaderboardData = useLeaderboard();
    const loading = allResults === undefined || leaderboardData === undefined;

    const getPositionBadgeColor = (position: number): 'warning' | 'neutral' | 'error' => {
        switch (position) {
            case 1: return 'warning'; // Gold
            case 2: return 'neutral'; // Silver
            case 3: return 'error';   // Bronze
            default: return 'neutral';
        }
    };

    const getPositionIcon = (position: number) => {
        switch (position) {
            case 1: return <Trophy className="w-5 h-5 text-yellow-500" />;
            case 2: return <Medal className="w-5 h-5 text-gray-300" />;
            case 3: return <Award className="w-5 h-5 text-orange-700" />;
            default: return <span className="font-mono font-bold text-gray-500">#{position}</span>;
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-[60vh]">
                <Loader size="lg" />
            </div>
        );
    }

    const myResults = allResults || [];
    const leaderboard = leaderboardData?.slice(0, 5) || [];

    return (
        <div className="space-y-8">
            {/* Header */}
            <div>
                <h1 className="text-4xl font-heading font-bold text-white mb-2 tracking-tight">
                    Event <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF991C] to-[#FFB84D]">Results</span>
                </h1>
                <p className="text-gray-400 text-lg">
                    Track your school's performance and overall standings
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Leaderboard */}
                <div className="lg:col-span-1">
                    <Card className="h-full border-white/5 bg-black/40 backdrop-blur-xl relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-b from-yellow-500/5 to-transparent pointer-events-none" />
                        <div className="p-6 relative z-10">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="p-2 rounded-lg bg-yellow-500/10 text-yellow-500">
                                    <Trophy className="w-6 h-6" />
                                </div>
                                <h2 className="text-xl font-heading font-bold text-white">Leaderboard</h2>
                            </div>

                            <div className="space-y-4">
                                {leaderboard.length === 0 ? (
                                    <p className="text-gray-500 text-center py-8">No results yet</p>
                                ) : (
                                    leaderboard.map((entry, index) => (
                                        <div
                                            key={entry.teamCode}
                                            className={`p-4 rounded-xl border transition-all ${entry.teamCode === school?.team_code
                                                    ? 'bg-brand/10 border-brand/50'
                                                    : 'bg-white/5 border-white/10'
                                                }`}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${index === 0 ? 'bg-yellow-500 text-black' :
                                                            index === 1 ? 'bg-gray-300 text-black' :
                                                                index === 2 ? 'bg-orange-700 text-white' :
                                                                    'bg-white/10 text-gray-400'
                                                        }`}>
                                                        {index + 1}
                                                    </div>
                                                    <span className={`font-bold ${entry.teamCode === school?.team_code ? 'text-brand' : 'text-white'}`}>
                                                        {entry.schoolName}
                                                    </span>
                                                </div>
                                                <span className="font-mono font-bold text-lg text-white">{entry.totalPoints}</span>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </Card>
                </div>

                {/* My Results */}
                <div className="lg:col-span-2">
                    <Card className="min-h-[500px] border-white/5 bg-black/40 backdrop-blur-xl relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-gold-500/5 to-transparent pointer-events-none" />
                        <div className="p-6 relative z-10">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="p-2 rounded-lg bg-gold-500/10 text-gold-500">
                                    <Award className="w-6 h-6" />
                                </div>
                                <h2 className="text-xl font-heading font-bold text-white">My School's Performance</h2>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-white/5 border-b border-white/10">
                                        <tr>
                                            <th className="px-6 py-4 text-left text-xs font-heading font-bold text-gray-400 uppercase tracking-widest">Event</th>
                                            <th className="px-6 py-4 text-left text-xs font-heading font-bold text-gray-400 uppercase tracking-widest">Position</th>
                                            <th className="px-6 py-4 text-right text-xs font-heading font-bold text-gray-400 uppercase tracking-widest">Points</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/5">
                                        {myResults.length === 0 ? (
                                            <tr>
                                                <td colSpan={3} className="px-6 py-16 text-center text-gray-500">
                                                    No results recorded for your school yet.
                                                </td>
                                            </tr>
                                        ) : (
                                            myResults.map((result) => (
                                                <tr key={result._id} className="hover:bg-white/5 transition-colors">
                                                    <td className="px-6 py-4 text-white font-medium">
                                                        {result.event_name}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-2">
                                                            {getPositionIcon(result.position)}
                                                            <Badge variant={getPositionBadgeColor(result.position)} size="sm">
                                                                {result.position === 1 ? 'Winner' :
                                                                    result.position === 2 ? 'Runner Up' :
                                                                        result.position === 3 ? 'Second Runner Up' : `${result.position}th Place`}
                                                            </Badge>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-right font-mono font-bold text-brand">
                                                        +{result.points}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};
